import { dexieDB } from "@/services/dexie-database";
import { LogLevel, LogService } from "@/services/logging";
import { ISmeetzPicture } from "@/types/db/product-category";

export enum SmeetzPictureEntityTables {
  Retail = "retail",
  Group = "group",
  Ticket = "ticket",
  PromoCode = "promo_code",
  Product = "product",
  ProductLogo = "product_logo",
  POSConfig = "pos_config",
  TicketLogo = "ticket_logo",
}

export enum SmeetzPictureEntityTypes {
  Retail = 1,
  Ticket = 3,
  Group = 4,
  PromoCode = 7,
  ProductLogo = 8,
  POSConfig = 9,
  TicketLogo = 10,
}

export const SmeetzPictureEntityTypeToTableMap: Record<
  SmeetzPictureEntityTypes,
  SmeetzPictureEntityTables
> = {
  [SmeetzPictureEntityTypes.Retail]: SmeetzPictureEntityTables.Retail,
  [SmeetzPictureEntityTypes.Ticket]: SmeetzPictureEntityTables.Ticket,
  [SmeetzPictureEntityTypes.Group]: SmeetzPictureEntityTables.Group,
  [SmeetzPictureEntityTypes.PromoCode]: SmeetzPictureEntityTables.PromoCode,
  [SmeetzPictureEntityTypes.ProductLogo]: SmeetzPictureEntityTables.ProductLogo,
  [SmeetzPictureEntityTypes.POSConfig]: SmeetzPictureEntityTables.POSConfig,
  [SmeetzPictureEntityTypes.TicketLogo]: SmeetzPictureEntityTables.TicketLogo,
};

/**
 *
 * Takes a picture metadata and returns a url to image proxy
 *
 */
export function createImageproxyUrl(
  payload: ISmeetzPicture | undefined,
  requestingSize: { width: number; height: number }
) {
  if (!payload) {
    return "";
  }
  const { metaData, name } = payload;

  const imgProxyUrlStart = import.meta.env.VITE_IMAGEPROXY_URLSTART || "";
  const metadataURL = getMetadataUrl(metaData, requestingSize);
  const base64url = geEncodedUrl(name);

  const imgProxyUrl = imgProxyUrlStart + metadataURL + base64url;
  return imgProxyUrl;
}

export function getMetadataUrl(
  metadata: string,
  requestingSize: { width: number; height: number }
) {
  // resize with filling option to XXXxYYY with enlarging if it is originally smaller, and without gravity
  const metadataUrl =
    "/rs:fill:" +
    requestingSize.width +
    ":" +
    requestingSize.height +
    ":1/g:no/";
  return metadataUrl;
}

export function geEncodedUrl(pictureUrl: string) {
  const s3url = import.meta.env.VITE_S3URL || "";
  const encodedUrl =
    btoa(s3url + "retail/" + pictureUrl)
      .replace(/=/g, "")
      .replace(/\+/g, "-")
      .replace(/\//g, "_") + ".png";
  return encodedUrl;
}

export function createCroppedImageproxyUrl(payload: any) {
  if (!payload) {
    return "";
  }
  const { name, metaData, table } = payload;
  const imgProxyUrlStart = import.meta.env.VITE_IMAGEPROXY_URLSTART || "";
  const metadataURL = `crop:${metaData.crop.width}:${metaData.crop.height}/gravity:${metaData.gravity.type}:${metaData.gravity.x_offset}:${metaData.gravity.y_offset}`;
  const base64url = geS3ImageEncodedUrl(name, table);

  return `${imgProxyUrlStart}/${metadataURL}/${base64url}`;
}

function geS3ImageEncodedUrl(name: string, table: string): string {
  // Trim the trailing slash before joining the object key
  const s3Url = (import.meta.env.VITE_S3URL || "").replace(/\/+$/, "");
  const encodedUrl = Buffer.from(`${s3Url}/${table}/${name}`)
    .toString("base64")
    .replace(/=/g, "")
    .replace(/\+/g, "-")
    .replace(/\//g, "_");

  return `${encodedUrl}.png`;
}

export async function getLocalPictureBase64(
  entityType: number,
  entityId: number
): Promise<string | null> {
  try {
    const picture = await dexieDB.pictures
      .where({ entityType: entityType, entityId: entityId })
      .first();

    if (!picture) {
      return null;
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(picture.imageBlob);
    });
  } catch (error) {
    LogService.info(
      "Picture: error getting base64 picture",
      { error },
      LogLevel.Error
    );
    return null;
  }
}
